import Illusion from 'illusionjs'
import Relationship from '.'
import Model from '../Model'
import QueryBuilder from '../QueryBuilder'
import { Builder } from '../Builder'

export class BelongsToMany extends Relationship {

    private _currentKey: string

    private _toManyKey: string

    private _mediatorKey: string

    private _mediatorBuilder: Builder & QueryBuilder

    private _invokeDefaultConstraints: boolean = false;

    private _isConstraintsForDeleteORRestore: boolean = false;

    private _mediatorModels: Model[] = []

    constructor(
        toManyBuilder: Builder & QueryBuilder,
        mediatorBuilder: Builder & QueryBuilder,
        currentModel: Model,
        toManyKey: string, 
        mediatorKey: string, 
        currentKey: string
    ) {

        super(toManyBuilder, currentModel)
        
        /** table below Many to Many relationship
         *  ________________         ___________________         _______________
         * | current_table  |       | mediator_table    |       | to_many_table |
         * |----------------|       |-------------------|       |---------------|
         * | id             |----|  | id                |   ----| id            |
         * | name           |    |->| current_table_id  |   |   | name          |
         * | ...            |       | to_many_table_id  | <--   | ...           |
        */

        /** 
         * current key is e.g "current_id" 
         * */
        this._currentKey = currentKey
        
         /** 
         * toMany key is e.g "toMany_id" 
         * */
        this._toManyKey = toManyKey

        /** 
         * mediator key is basically just an "id" 
         * */
        this._mediatorKey = mediatorKey
        
        this._mediatorBuilder = mediatorBuilder

        this.requiringDefaultConstraints()
    }

    public async findOrNew<A = any>(toManyID: number, attributes?: A): Promise<Model> {

		const currentModel = this.getCurrentModel()

		const mediatorModel = this.getMediatorModel()

        const toManyKey = this.getToManyKey()
        
		const currentKey = this.getCurrentKey()

		const currentKeyId = currentModel.getPrimaryKeyId()

		const collections = await this.getMediatorBuilder().where(currentKey, currentKeyId).where(toManyKey, toManyID).first()

		return Object.assign(
            collections.hasItem ? collections.getItems() : mediatorModel,
			{
                ...attributes, 
                [currentKey]: currentKeyId,
                [toManyKey]: toManyID
            }
		)
    }

    public async findAndDelete(toManyID: number) {

		const currentModel = this.getCurrentModel()

        const toManyKey = this.getToManyKey()
        
		const currentKey = this.getCurrentKey()

		const currentKeyId = currentModel.getPrimaryKeyId()

		return this.getMediatorBuilder().where(currentKey, currentKeyId).where(toManyKey, toManyID).delete()

    }

    public async findAndSoftDelete(toManyID: number) {

		const currentModel = this.getCurrentModel()

        const toManyKey = this.getToManyKey()
        
		const currentKey = this.getCurrentKey()

		const currentKeyId = currentModel.getPrimaryKeyId()

		return this.getMediatorBuilder().where(currentKey, currentKeyId).where(toManyKey, toManyID).softDelete()

    }

    public async findAndRestore(toManyID: number) {

		const currentModel = this.getCurrentModel()

        const toManyKey = this.getToManyKey()
        
		const currentKey = this.getCurrentKey()

		const currentKeyId = currentModel.getPrimaryKeyId()

		return this.getMediatorBuilder().where(currentKey, currentKeyId).where(toManyKey, toManyID).restore()

    }

    private getMediatorBuilder() {

        if(!this._mediatorBuilder.getPool()) {

            const pool = this.getRelativeBuilder().getPool()
    
            if(pool) {
                this._mediatorBuilder.pool(pool)
            }

        }

        return this._mediatorBuilder
    }

    private getMediatorModel() {

        return (this.getMediatorBuilder() as any).getModel()
    }

    public requiringDefaultConstraints () {

        if (Relationship.constraints) {

            this._invokeDefaultConstraints = true

        }
    }
    
    public async manuallyAddDefaultConstraints(): Promise<void> {

        const toManyIDs = await this.getDefaultConstraintsToManyIDs()
        
		this.getRelativeBuilder().whereIn(this.getRelativeModel().getAliasedKey(), toManyIDs.length > 0 ? toManyIDs : [0])

    }

    private getDefaultConstraintsMediatorModels (): Promise<Model[]> {

        this.getMediatorBuilder()
            .select(this.getMediatorKey(), this.getToManyKey(), this.getCurrentKey())
            .where(this.getCurrentKey(), this.getCurrentModel().getPrimaryKeyId())
        
        return this.getMediatorBuilder().getQueryBuilder().get()
    }

    private async getDefaultConstraintsToManyIDs () {

        const mediatorModels = await this.getDefaultConstraintsMediatorModels()

        this._mediatorModels = mediatorModels

        return this.getToManyModelIDs(mediatorModels)
    }

    protected getToManyKey(): string {

        return this._toManyKey
    }

    protected getMediatorKey(): string {

        return this._mediatorKey
    }

    protected getCurrentKey(): string {
        
        return this._currentKey
	}
	
	public async addPersistentConstraints(currentModels: Model[]): Promise<void> {
        
        const currentModelsToManyIDs = await this.getCurrentModelsToManyIDs(currentModels)
        
        this.getRelativeBuilder().whereIn(this.getRelativeModel().getAliasedKey(), currentModelsToManyIDs.length > 0 ? currentModelsToManyIDs : [0])
    }

    public async addPersistentConstraintsForDelete(currentModels: Model[]): Promise<void> {

        this._isConstraintsForDeleteORRestore = true

        this.getMediatorBuilder().whereIn(this.getCurrentKey(), this.getModelsId(currentModels))
    }

    public async addPersistentConstraintsForRestore(currentModels: Model[]): Promise<void> {

        this._isConstraintsForDeleteORRestore = true

        this.getMediatorBuilder().whereIn(this.getCurrentKey(), this.getModelsId(currentModels))
    }
    
    private async getCurrentMediatorModels(currentModels: Model[]): Promise<any[]> {
        
        this.getMediatorBuilder()
            .select(this.getMediatorKey(), this.getToManyKey(), this.getCurrentKey())
            .whereIn(this.getCurrentKey(), this.getModelsId(currentModels))

        return this.getMediatorBuilder().getQueryBuilder().get()
    }

    private async getCurrentModelsToManyIDs(currentModels: Model[]): Promise<number[]> {

        const mediatorModels = await this.getCurrentMediatorModels(currentModels)

        this._mediatorModels = mediatorModels

        return this.getToManyModelIDs(mediatorModels)
    }

    private getToManyModelIDs(models: Model[]): number[] {

        return this.excludeDuplicate(models.map((model: any) => {

            return model[this.getToManyKey()]
        }))
    }

	protected match(currentModels: Model[], relativeModels: Model[], relativeName: string): boolean {

		const groupRelativeResultByForeignKey = this.setForeignKeyAsResultIndex(relativeModels)

        currentModels.forEach((model: Model) => {

			const currentModelID = this.getModelId(model)

            // if(currentModelID in groupRelativeResultByForeignKey) {

                model.setRelative(relativeName, groupRelativeResultByForeignKey[currentModelID] || [])
            // }
        })

        return true
	}

	protected setForeignKeyAsResultIndex(toManyModels: Model[]): Record<any, any> {

        const mediatorModelsByToManyID = this.mediatorModelsByToManyID()
	
        const currentKey = this.getCurrentKey()

        const groupRelativeResultByCurrentKey = toManyModels.reduce<Record<any, any>>((result, row) => {
            
            const mediatorModel = mediatorModelsByToManyID[row.getPrimaryKeyId()]

            const currentModelID: any = mediatorModel[currentKey as keyof Model]

            if(currentModelID in result) {

                result[currentModelID].push(row)

            } else {
                
                result[currentModelID] = [row]
            }

            return result
		}, {})
		
		return groupRelativeResultByCurrentKey
    }

    private mediatorModelsByToManyID(): Record<any, any>  {

        const mediatorModels = this._mediatorModels as any[]

        const toManyKey = this.getToManyKey()

        return mediatorModels.reduce<Record<any, any>>((result, item) => {

            result[item[toManyKey]] = item
            
            return result
        }, {})
    }
    
    /**
     * 
     * @param method illusion override Relationship __get
     */
    public __get(method: string): any {

        const builder = this._isConstraintsForDeleteORRestore ? this.getMediatorBuilder() : this.getRelativeBuilder()

		if (builder[method as keyof (Builder & QueryBuilder)]) {

            /**
             * when the method called was 'get', 'first', 'paginate' & 'delete'
             * we need to invoke the manuallyAddDefaultConstraints
             * the method is async which we can't put it on Constructor as initial invokation
             * so we need to invoke it before we query in SQL to chain the Promise
             */

            if(this._invokeDefaultConstraints && ['get', 'first', 'paginate', 'delete', 'softDelete', 'restore'].includes(method)) {

                return async (...parameters: any[]) => {

                    await this.manuallyAddDefaultConstraints()
    
                    return await (builder as any)[method](...parameters)
    
                }

            } else {

                return (...parameters: any[]) => {
    
                    const result = (builder as any)[method](...parameters)
    
                    if (result === builder) return this;
    
                    return result;
    
                }
            }


        } else {

            throw new Error(`${this.getRelativeModel().getConstructor().name}.${method} does not exists`)
            
		}
	}

}

export default Illusion<typeof BelongsToMany>(BelongsToMany) 